import { Button } from "primereact/button";
import { useContext } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useError } from "src/core/hooks/useError";
import { EmailField } from "src/features/shared/components/EmailField";
import { PasswordField } from "src/features/shared/components/PasswordField";
import { ToastContext } from "../../../../core/context/ToastContext";
import { useErrorHandler } from "../../../../core/error/useErrorHandler";
import { logService } from "../../../../core/services/logService";
import { useAuthStore } from "../../services/useAuthStore";
import "./Login.css";

type LoginProps = {
  registerVisible?: boolean;
  forgotPasswordVisible?: boolean;
  afterLogin?: () => void;
};

type FormData = {
  email: string;
  password: string;
};

export function Login({
  registerVisible = true,
  forgotPasswordVisible = true,
  afterLogin = () => {},
}: LoginProps) {
  const toastRef = useContext(ToastContext);

  const form = useForm<FormData>({
    mode: "all",
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getErrorMessage } = useError();

  const authStore = useAuthStore();
  const errorHandler = useErrorHandler();

  const onLogin = async () => {
    if (!form.formState.isValid) return;

    await authStore
      .login({
        email: form.getValues("email"),
        password: form.getValues("password"),
      })
      .then((user) => {
        logService.log("User is logged in:", user);

        toastRef?.current?.show({
          severity: "success",
          summary: t("Home.Login.Title"),
          detail: t("Home.Login.Welcome", { value: user.name }),
        });

        afterLogin();
      })
      .catch((error) => {
        errorHandler.setError({
          title: t("Home.Login.Title"),
          message: t("Home.Login.Error") + " " + getErrorMessage(error),
        });
      });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (form.formState.isValid && e.key === "Enter") onLogin();
  };

  const onForgotPassword = () => {
    const email = form.getValues("email");
    if (!email) {
      errorHandler.setError({
        title: t("Home.ResetPassword.Title"),
        message: t("Error.EmailRequired"),
      });
      return;
    }

    authStore
      .forgotPassword(email)
      .then(() => {
        toastRef?.current?.show({
          severity: "success",
          summary: t("Home.ResetPassword.Title"),
          detail: t("Home.ResetPassword.Message"),
          life: 10000,
        });
      })
      .catch((error) => {
        errorHandler.setError({
          title: t("Home.ResetPassword.Title"),
          message: t("Home.ResetPassword.Error") + " " + getErrorMessage(error),
        });
      });
  };

  return (
    <form>
      <div className="login h100 overflow-auto">
        <div className="flex flex-column gap-2">
          <div className="p-inputgroup">
            <span className="p-inputgroup-addon">
              <i className="pi pi-envelope"></i>
            </span>

            <EmailField
              form={form}
              required={true}
              errorField={false}
              onKeyDown={onKeyDown}
            />
          </div>

          <div className="p-inputgroup">
            <span className="p-inputgroup-addon">
              <i className="pi pi-lock"></i>
            </span>

            <PasswordField
              form={form}
              required={true}
              errorField={false}
              feedback={false}
              onKeyDown={onKeyDown}
            />
          </div>

          <Button
            type="button"
            label={t("Home.Login.Title")}
            icon="pi pi-check"
            disabled={!form.formState.isValid}
            loading={authStore.loading}
            className="flex justify-content-center w100"
            onClick={onLogin}
          ></Button>

          {registerVisible && (
            <Button
              type="button"
              label={t("Home.Registration.Title")}
              icon="pi pi-id-card"
              link
              className="flex justify-content-center w100"
              onClick={() => {
                navigate("/register");
              }}
            ></Button>
          )}

          {forgotPasswordVisible && (
            <Button
              type="button"
              label={t("Home.ResetPassword.Title")}
              icon="pi pi-key"
              link
              className="flex justify-content-center w100"
              loading={authStore.loadingForgotPassword}
              onClick={onForgotPassword}
            ></Button>
          )}
        </div>
      </div>
    </form>
  );
}
